import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'tawang', name: 'Tawang', homestays: 280, valleys: ['Tawang Monastery', 'Sela Pass', 'PTSO Lake', 'Bum La'], altitude: '2,600–5,500m', highlight: true, division: 'West' },
  { id: 'westkameng', name: 'West Kameng', homestays: 220, valleys: ['Dirang', 'Bomdila', 'Sangti Valley', 'Mandala'], altitude: '1,500–4,700m', highlight: true, division: 'West' },
  { id: 'ziro', name: 'Lower Subansiri', homestays: 210, valleys: ['Ziro Valley', 'Apatani Plateau', 'Talley Valley', 'Hapoli'], altitude: '1,500–3,000m', highlight: true, division: 'Central' },
  { id: 'siang', name: 'Siang Valley', homestays: 190, valleys: ['Pasighat', 'Along', 'Mechuka', 'Tuting'], altitude: '150–4,500m', highlight: true, division: 'Central' },
  { id: 'papumpare', name: 'Papum Pare', homestays: 170, valleys: ['Itanagar', 'Naharlagun', 'Sagalee', 'Doimukh'], altitude: '200–2,500m', highlight: false, division: 'Central' },
  { id: 'lohit', name: 'Lohit & Anjaw', homestays: 130, valleys: ['Tezu', 'Hayuliang', 'Dong Village', 'Walong'], altitude: '300–4,000m', highlight: true, division: 'East' },
  { id: 'dibang', name: 'Dibang Valley', homestays: 110, valleys: ['Anini', 'Roing', 'Mayodia Pass', 'Mehao Lake'], altitude: '400–5,000m', highlight: false, division: 'East' },
  { id: 'changlang', name: 'Changlang & Namsai', homestays: 100, valleys: ['Namdapha NP', 'Namsai', 'Golden Pagoda', 'Miao'], altitude: '200–4,500m', highlight: true, division: 'Southeast' },
  { id: 'eastkameng', name: 'East Kameng', homestays: 80, valleys: ['Seppa', 'Pakke Tiger Reserve', 'Bichom'], altitude: '500–3,500m', highlight: false, division: 'West' },
  { id: 'tirap', name: 'Tirap & Longding', homestays: 60, valleys: ['Khonsa', 'Longding', 'Wancho Villages'], altitude: '500–2,500m', highlight: false, division: 'Southeast' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Arunachal Pradesh Tourism Department, digitized and made discoverable across 28 districts and 26 tribal regions.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on region map', 'Basic contact listing', 'Searchable by valley & tribe'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified photos, authentic tribal experiences, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden treasures of India\'s frontier.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay across Arunachal\'s 28 districts — from Tawang\'s monasteries to Dong\'s first sunrise — searchable, verified, bookable.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Inner Line Permit trends, and region-level occupancy insights for tourism planning in India\'s least explored state.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering 26 tribal communities as hosts, preserving traditions while building livelihoods.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification maintaining standards while honouring the sacred traditions and communal land systems of each tribe.' },
];

function ArunachalMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'tawang': { d: 'M30 50 L100 30 L140 60 L130 110 L80 120 L40 95 Z', cx: 85, cy: 75 },
    'westkameng': { d: 'M80 120 L130 110 L170 130 L180 180 L140 200 L100 180 L70 150 Z', cx: 125, cy: 155 },
    'eastkameng': { d: 'M140 200 L180 180 L230 190 L240 230 L200 250 L160 235 Z', cx: 193, cy: 215 },
    'papumpare': { d: 'M200 250 L240 230 L290 240 L300 280 L260 295 L220 280 Z', cx: 255, cy: 265 },
    'ziro': { d: 'M140 60 L220 40 L260 80 L250 130 L200 150 L170 130 Z', cx: 200, cy: 95 },
    'siang': { d: 'M250 130 L330 100 L380 140 L370 200 L320 220 L280 200 L260 160 Z', cx: 315, cy: 165 },
    'dibang': { d: 'M330 100 L420 80 L460 120 L450 180 L400 200 L370 200 L380 140 Z', cx: 405, cy: 140 },
    'lohit': { d: 'M400 200 L450 180 L510 200 L520 250 L480 270 L430 260 Z', cx: 465, cy: 228 },
    'changlang': { d: 'M370 200 L430 260 L480 270 L490 310 L440 330 L380 310 L340 280 L320 250 Z', cx: 410, cy: 278 },
    'tirap': { d: 'M340 280 L380 310 L440 330 L420 360 L360 360 L320 340 Z', cx: 375, cy: 330 },
  };

  return (
    <svg viewBox="0 0 540 380" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path
              d={path.d}
              fill={isActive ? '#B85C38' : isHighlight ? 'rgba(184,92,56,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#E8A87C' : 'rgba(255,255,255,0.3)'}
              strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }}
            />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 11 : 9}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 15} textAnchor="middle" dominantBaseline="middle"
                fill="#E8A87C" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function ArunachalStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="arunachal-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="arunachal" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1772089003269-ddcd0a6665ae?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Arunachal Pradesh &middot; Land of the Rising Sun</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 28 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where India sees its first sunrise,<br />and 26 tribes keep the mountains awake
          </h2>
        </div>
      </section>

      {/* Story */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#E8A87C] text-[10px] tracking-[0.5em] uppercase mb-6">The Dawnlit Mountains</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Before the rest of India wakes,<br />
            Arunachal has already lit the hearth.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            In Dong village, India's easternmost settlement, the sun arrives before anywhere else 
            in the country. In Tawang, the world's second-largest Buddhist monastery stands 
            where clouds walk through corridors. In Ziro Valley, the Apatani people tend rice 
            paddies in a landscape UNESCO calls a living heritage. In Mechuka, a valley so remote 
            it was only connected by road in 2005, Memba families serve butter tea in bamboo homes 
            while the Siyom river thunders below. Twenty-six major tribes, a hundred sub-tribes, 
            each with its own language, its own festival, its own way of reading the forest. 
            This is India's last great frontier — and its homestays are the only doors into it.
          </p>
        </div>
      </section>

      {/* Living Map */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#E8A87C] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              28 districts. 26 tribes. One frontier.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <ArunachalMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#E8A87C] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Arunachal</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Arunachal</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#B85C38] to-[#E8A87C] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#E8A87C] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise through the ranks — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#E8A87C] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner for state tourism departments — bringing every licensed homestay online, making them discoverable to millions of travellers, and providing real-time demand intelligence for tourism planning.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#E8A87C] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#E8A87C]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(232,168,124,0.05), transparent)' }}>
            <p className="text-[#E8A87C] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#E8A87C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#E8A87C] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Arunachal shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 28 districts</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#E8A87C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#E8A87C] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into tribal communities and frontier villages</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#E8A87C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#E8A87C] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, tribal tourism analytics, and sustainable development planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Arunachal%20Pradesh."
              className="inline-flex items-center gap-3 bg-[#E8A87C] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#f0bd97] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#E8A87C" />

      <style>{`
        @keyframes expandBar {
          from { width: 0; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
